/* SPDX-License-Identifier: Apache-2.0 */
import {GirafeHTMLElement} from '@geogirafe/lib-geoportal/base';

/**
 * Maps a `data-visibility-rule` value to the list of theme names (technical
 * theme "name", not the display title) for which the linked element(s)
 * must be visible.
 */
type VisibilityRules = Record<string, string[]>;

/**
 * Headless component (no visual output) that shows/hides other elements
 * (typically buttons in #buttonbar) depending on the theme currently
 * displayed in the geoportal.
 *
 * Elements are linked to a rule via the `data-visibility-rule="<ruleName>"`
 * attribute. The rules themselves come from `extendedConfig.visibilityManager.rules`
 * in config.json. An element whose rule isn't matched by the active theme
 * stays hidden, so it must default to `class="hidden"` in the HTML markup.
 */
class SitnVisibilityManagerComponent extends GirafeHTMLElement {
  templateUrl = null;
  styleUrls = null;

  constructor() {
    super('sitn-visibility-manager');
  }

  override connectedCallback() {
    super.connectedCallback();
    this.renderEmpty();
    this.subscribe('themes.lastSelectedTheme', () => this.updateVisibility());
    this.updateVisibility();
  }

  private get rules(): VisibilityRules {
    const extConfig = this.context.configManager.Config.extendedConfig as any;
    return extConfig?.['visibilityManager']?.rules ?? {};
  }

  private updateVisibility() {
    const currentThemeName = this.state.themes.lastSelectedTheme?.name;
    for (const [ruleName, themeNames] of Object.entries(this.rules)) {
      const isVisible = currentThemeName != null && themeNames.includes(currentThemeName);
      document.querySelectorAll(`[data-visibility-rule="${ruleName}"]`).forEach((element) => {
        element.classList.toggle('hidden', !isVisible);
      });
    }
  }
}

export default SitnVisibilityManagerComponent;
